import { faCheck, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "@reduxjs/toolkit";
import { User } from "~/modules/user/store/types";
import { cn } from "../../lib/utils";
import { Membership } from "~/modules/user/store/types";
import { fetchInvitations } from "~/modules/user/store";
import { api } from "~/utils/api";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { Modal } from "~/components/Modal";
import {
    trackInvitationAccept,
    trackInvitationReject,
} from "~/modules/auth/analytics";

// // // //

/**
 * Individual notification item for a team invitation
 */
function InvitationItem(props: {
    invite: Membership;
    loading: boolean;
    onAccept: (inviteId: string) => void;
    onReject: (inviteId: string) => void;
}) {
    const { invite, loading, onAccept, onReject } = props;

    return (
        <div className="px-4 py-3 border-b last:border-b-0">
            <div className="flex flex-col gap-2">
                <div className="text-sm text-foreground">
                    <span className="font-medium">
                        {invite.organization.name}
                    </span>
                    <p className="text-foreground/60 mt-1">
                        invited you to join their team
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => onAccept(invite.id)}
                        disabled={loading}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            "bg-green-500/10 text-green-600 hover:bg-green-500/20",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                        )}
                        aria-label={`Accept invitation from ${invite.organization.name}`}
                    >
                        <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
                        Accept
                    </button>
                    <button
                        onClick={() => onReject(invite.id)}
                        disabled={loading}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            "bg-red-500/10 text-red-600 hover:bg-red-500/20",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                        )}
                        aria-label={`Reject invitation from ${invite.organization.name}`}
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                        Reject
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Empty state when there are no notifications
 */
function EmptyNotifications() {
    return (
        <div className="px-4 py-8 text-center">
            <p className="text-sm text-foreground/40">No pending invites</p>
        </div>
    );
}

/**
 * Notification items list
 */
function UserPendingInvitationsItems(props: {
    invites: Membership[];
    loading: boolean;
    onAccept: (inviteId: string) => void;
    onReject: (inviteId: string) => void;
}) {
    const { invites, loading, onAccept, onReject } = props;

    return (
        <>
            <div className="px-4 py-3 border-b">
                <p className="text-lg font-medium text-foreground">
                    Team Invites
                </p>
                {invites.length > 0 && (
                    <p className="text-xs text-foreground/60 mt-0.5">
                        {invites.length} pending{" "}
                        {invites.length === 1 ? "invitation" : "invitations"}
                    </p>
                )}
            </div>

            {invites.length === 0 ? (
                <EmptyNotifications />
            ) : (
                invites.map((invite) => (
                    <InvitationItem
                        key={invite.id}
                        invite={invite}
                        loading={loading}
                        onAccept={onAccept}
                        onReject={onReject}
                    />
                ))
            )}
        </>
    );
}

export function UserPendingInvitationsModal(props: {
    open: boolean;
    invites: Membership[];
    loading: boolean;
    onAccept: (inviteId: string) => void;
    onReject: (inviteId: string) => void;
}) {
    const { open, invites, loading, onAccept, onReject } = props;

    return (
        <Modal show={open} size="sm" allowScroll={true} allowClose={false}>
            <div className="w-120 max-w-full bg-card border shadow-2xl rounded-lg text-left">
                <UserPendingInvitationsItems
                    invites={invites}
                    loading={loading}
                    onAccept={onAccept}
                    onReject={onReject}
                />
            </div>
        </Modal>
    );
}

/**
 * User notifications dropdown menu for displaying and managing team invitations
 */
export function UserPendingInvitations(props: { user: User }) {
    const { user } = props;
    const { addToaster } = useToaster();
    const dispatch: ThunkDispatch<{}, {}, AnyAction> = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(true);

    // Define the acceptance and rejection mutations
    const acceptInvitationMutation = api.users.acceptInvitation.useMutation();
    const declineInvitationMutation = api.users.declineInvitation.useMutation();

    // Fetch invitations on mount
    useEffect(() => {
        dispatch(fetchInvitations());
    }, [dispatch]);

    // If the user is not logged in, return null
    if (!user.email) {
        return null;
    }

    // Get the user's pending invitations
    const invites = user.pending_invitations;
    const hasNotifications = invites.length > 0;

    // If the user has no pending invitations, return null
    if (!hasNotifications) {
        return null;
    }

    // Define loading state based on the acceptance and rejection mutations
    const isLoading =
        acceptInvitationMutation.isPending ||
        declineInvitationMutation.isPending;

    // Handle accepting an invitation
    const handleAcceptInvite = async (inviteId: string) => {
        const invite = invites.find((inv) => inv.id === inviteId);
        if (!invite) return;

        // Track invitation accept
        trackInvitationAccept({
            organizationId: invite.organization.id,
            organizationName: invite.organization.name,
        });

        try {
            await acceptInvitationMutation.mutateAsync({
                membershipId: inviteId,
            });

            // Refresh the page to update memberships, organization, and features
            window.location.reload();

            addToaster({
                variant: "success",
                message: "Successfully accepted invitation",
            });
        } catch (error) {
            addToaster({
                variant: "danger",
                message:
                    error instanceof Error
                        ? error.message
                        : "Failed to accept invitation",
            });
        }
    };

    // Handle rejecting an invitation
    const handleRejectInvite = async (inviteId: string) => {
        const invite = invites.find((inv) => inv.id === inviteId);
        if (!invite) return;

        // Track invitation reject
        trackInvitationReject({
            organizationId: invite.organization.id,
            organizationName: invite.organization.name,
        });

        try {
            await declineInvitationMutation.mutateAsync({
                membershipId: inviteId,
            });

            // Refresh invitations after declining
            dispatch(fetchInvitations());

            addToaster({
                variant: "success",
                message: "Successfully declined invitation",
            });
        } catch (error) {
            addToaster({
                variant: "danger",
                message:
                    error instanceof Error
                        ? error.message
                        : "Failed to decline invitation",
            });
        }
    };

    return (
        <UserPendingInvitationsModal
            open={isModalOpen}
            invites={invites}
            loading={isLoading}
            onAccept={handleAcceptInvite}
            onReject={handleRejectInvite}
        />
    );
}
